import { useSuspenseQuery } from '~/services/queries'
import { Fragment, useEffect, useState } from 'react'
import { User } from '~/features/users/types'
import type { WorkspaceMember } from '@botpress/client'
import { Avatar, Badge, EmptyState, Spinner } from '~/elements'
import { Button, Card, Separator, Checkbox } from '@bpinternal/ui-kit-next'
import { useUpdateBotAllowlist } from '~/hooks/bots/useUpdateBotAllowlist'
import ForbiddenIcon from '~/assets/website-maintenance-15.svg?react'
import { useMap } from 'usehooks-ts'
import { useIsAuthorized } from '~/hooks'
import useNavigationBlocker, { defaultBlockerOptions } from '~/features/webchat/hooks/useNavigationBlocker'

const memberAlwaysHasAccess = (member: WorkspaceMember) => member.role === 'administrator' || member.role === 'owner'

type BotAccessControlProps = {
  botId: string
  workspaceId: string
  currentUser: User
}

export function BotAccessControl({ botId, currentUser, workspaceId }: BotAccessControlProps): JSX.Element {
  const workspaceMembers = useSuspenseQuery('workspaces_/$workspaceId_/members', { workspaceId }).data
  const ownerAndAdmins = workspaceMembers.filter(memberAlwaysHasAccess).filter((m) => !!m.userId) as Array<
    Omit<WorkspaceMember, 'userId'> & { userId: string }
  >
  const otherWorkspaceMembers = workspaceMembers
    .filter((m) => !memberAlwaysHasAccess(m))
    .filter((m) => !!m.userId) as Array<Omit<WorkspaceMember, 'userId'> & { userId: string }>
  const { data: allowlist, isRefetching: isAllowlistRefetching } = useSuspenseQuery(
    'workspaces_/$workspaceId_/bots_/$botId_/allowlist',
    {
      workspaceId,
      botId,
    }
  )
  const { mutateAsync: updateAllowList, isPending } = useUpdateBotAllowlist()
  const isAuthorized = useIsAuthorized({ workspaceId, userId: currentUser.id })

  const [checkedMap, { set: setIsChecked, reset: resetIsChecked }] = useMap<string, boolean>()
  const [setting, setSetting] = useState(allowlist.setting)
  const [hasSubmitted, setHasSubmitted] = useState(false)

  const isChecked = (userId: string): boolean => {
    const checked =
      setting === 'ALL_WORKSPACE_USERS' ||
      (checkedMap.has(userId) ? checkedMap.get(userId)! : allowlist.users.some((user) => user.id === userId))
    return checked
  }

  const hasPendingChanges = checkedMap.size > 0 || setting !== allowlist.setting

  const getCheckedUsers = () =>
    [...checkedMap.entries()].reduce(
      (acc, [userId, isChecked]) => {
        acc[userId] = isChecked
        return acc
      },
      {} as Record<string, boolean>
    )

  const onSubmit = () => {
    const users = setting === 'SELECTED_USERS' ? getCheckedUsers() : undefined

    updateAllowList({
      workspaceId,
      botId,
      setting,
      users,
    }).then(() => {
      setHasSubmitted(true)
    })
  }

  useEffect(() => {
    // Only clear the modified state once we're certain that the new server state is available.
    // Otherwise, we witness checkbox flickers.
    if (hasSubmitted && !isAllowlistRefetching) {
      resetIsChecked()
      setHasSubmitted(false)
      setSetting(allowlist.setting)
    }
  }, [hasSubmitted, resetIsChecked, isAllowlistRefetching, allowlist.setting])

  useNavigationBlocker(hasPendingChanges)

  if (!isAuthorized('workspace.manageMembers')) {
    return (
      <div className="mx-auto flex h-full w-full max-w-lg">
        <EmptyState
          iconSize={6}
          icon={ForbiddenIcon}
          title={'Access Denied'}
          description={
            'You don’t have permission to access this content. Please check your access rights and verify your credentials, or contact your administrator.'
          }
        />
      </div>
    )
  }

  const isAllChecked = (): boolean => {
    if (setting === 'ALL_WORKSPACE_USERS') {
      return true
    }

    return otherWorkspaceMembers.every((member) => (member.userId ? isChecked(member.userId) : false))
  }

  const toggleAllChecked = () => {
    if (setting === 'ALL_WORKSPACE_USERS') {
      setSetting('SELECTED_USERS')
      for (const member of otherWorkspaceMembers) {
        setIsChecked(member.userId, false)
      }
    } else {
      if (!isAllChecked()) {
        resetIsChecked()
        setSetting('ALL_WORKSPACE_USERS')
      } else {
        for (const member of otherWorkspaceMembers) {
          setIsChecked(member.userId, false)
        }
      }
    }
  }

  const checkMember = (userId: string) => {
    if (setting === 'ALL_WORKSPACE_USERS') {
      setSetting('SELECTED_USERS')
      for (const member of otherWorkspaceMembers) {
        if (member.userId === userId) {
          continue
        }

        setIsChecked(member.userId, true)
      }
    } else {
      setIsChecked(userId, !isChecked(userId))
    }
  }

  return (
    <div className="flex max-w-5xl flex-col-reverse gap-4">
      <div className="flex flex-col gap-2">
        <div className="mb-4 flex flex-col">
          <h3 className="text-lg font-medium text-neutral-950">Default Access</h3>
          <p className="text-sm">Owners and Administrators always have access to all bots in a workspace.</p>
        </div>
        {ownerAndAdmins.map((member) => (
          <OwnerAdminRow key={member.userId} member={member} currentUser={currentUser} />
        ))}
      </div>
      <div className="flex max-w-5xl flex-col gap-4">
        {otherWorkspaceMembers.length > 0 && (
          <>
            <div className="flex flex-col">
              <div className="mb-4">
                <h2 className="text-lg font-medium text-neutral-950">Workspace Members</h2>
                <p className="text-sm">Select which workspace members can access this bot.</p>
              </div>
              <Card className="text-sm">
                <div className="relative gap-x-7">
                  <div className="flex items-center gap-2 rounded-t-xl bg-neutral-50 px-3 py-2 font-medium">
                    <Checkbox
                      checked={setting === 'ALL_WORKSPACE_USERS' || isAllChecked()}
                      onCheckedChange={toggleAllChecked}
                      className="border-neutral-500"
                    />
                    <p>Select all</p>
                  </div>
                  {otherWorkspaceMembers.map((member) => (
                    <Fragment key={member.id}>
                      <Separator />
                      <MemberRow
                        member={member}
                        currentUser={currentUser}
                        checked={isChecked(member.userId)}
                        onToggle={(userId) => {
                          checkMember(userId)
                        }}
                      />
                    </Fragment>
                  ))}
                </div>
              </Card>
            </div>
            <Button
              disabled={isPending || !hasPendingChanges || hasSubmitted}
              onClick={onSubmit}
              className="ml-auto w-16"
            >
              {isPending ? <Spinner size="2" color="white" /> : 'Save'}
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

type MemberRowProps = {
  member: Omit<WorkspaceMember, 'userId'> & { userId: string }
  currentUser: User
  onToggle: (userId: string) => void
  checked: boolean
  disabled?: boolean
}

function MemberRow({ member, currentUser, onToggle, checked, disabled }: MemberRowProps) {
  const { email, role, userId, displayName, profilePicture } = member

  return (
    <div className="grid grid-cols-[auto_auto_auto_1fr_auto] items-center gap-4 p-3">
      {disabled ? (
        <Checkbox checked={true} disabled />
      ) : (
        <Checkbox checked={checked} onClick={() => onToggle(userId)} className="border-neutral-500" />
      )}
      <Avatar name={displayName ?? email} pictureUrl={profilePicture} className="h-8 w-8 rounded-sm" variant="solid" />
      <span className="text-sm font-medium">{email}</span>
      <div className="flex gap-2">
        {currentUser.email === email && (
          <Badge variant="outline" color="blue">
            You
          </Badge>
        )}
        {role === 'owner' && <Badge variant="secondary">Owner</Badge>}
      </div>
      <span className="text-sm text-muted-foreground">{role}</span>
    </div>
  )
}

type OwnerAdminRowProps = {
  member: Omit<WorkspaceMember, 'userId'> & { userId: string }
  currentUser: User
}

function OwnerAdminRow({ member, currentUser }: OwnerAdminRowProps) {
  const { email, role, displayName, profilePicture } = member

  return (
    <div className="grid grid-cols-[auto_auto_1fr_auto] items-center gap-4">
      <Avatar name={displayName ?? email} pictureUrl={profilePicture} className="h-8 w-8 rounded-sm" variant="solid" />
      <span className="text-sm font-medium">{email}</span>
      <div className="flex gap-2">
        {currentUser.email === email && (
          <Badge variant="outline" color="blue">
            You
          </Badge>
        )}
        {role === 'owner' && <Badge variant="secondary">Owner</Badge>}
      </div>
      <span className="text-sm text-muted-foreground">{role}</span>
    </div>
  )
}
